import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Field, Icon, Segmented, Select, Slider, Switch, Textarea, Tooltip, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { effectiveAudiences, viewerAudiences } from "../shared/audience-presets";
import { useModels } from "../shared/queries";
import type { AgentUpdate, CitationStyle, PlaygroundConfig } from "../shared/types";
import { useSettingsMutations } from "../agent-settings/use-agent-settings";
import { estimateTokens } from "./trace-model";

const CITATIONS: readonly CitationStyle[] = ["numbered", "source_names", "hidden"];
const AUDIENCES = [1, 2, 3, 4] as const;
const TEMPERATURE_STEP = 0.1;

interface Draft {
  model_id: string;
  temperature: number;
  strict: boolean;
  citation_style: CitationStyle;
  reply_in_user_language: boolean;
  /** `null` without agents.view: the instructions are neither shown nor saved. */
  instructions: string | null;
}

type DraftSource = Pick<PlaygroundConfig, "model_id" | "effective_model" | "temperature" | "grounding_mode" | "citation_style" | "reply_in_user_language">;

function draftOf(config: DraftSource, instructions: string | null): Draft {
  return {
    model_id: config.effective_model?.id ?? config.model_id ?? "",
    temperature: config.temperature,
    strict: config.grounding_mode === "strict",
    citation_style: config.citation_style as CitationStyle,
    reply_in_user_language: config.reply_in_user_language,
    instructions,
  };
}

function patchOf(draft: Draft, base: Draft): AgentUpdate {
  const body: AgentUpdate = {};
  if (draft.model_id !== base.model_id && draft.model_id) body.model_id = draft.model_id;
  if (draft.temperature !== base.temperature) body.temperature = draft.temperature;
  if (draft.strict !== base.strict) body.grounding_mode = draft.strict ? "strict" : "balanced";
  if (draft.citation_style !== base.citation_style) body.citation_style = draft.citation_style;
  if (draft.reply_in_user_language !== base.reply_in_user_language) body.reply_in_user_language = draft.reply_in_user_language;
  if (draft.instructions !== null && draft.instructions !== base.instructions) body.instructions = draft.instructions;
  return body;
}

interface ConfigPanelProps {
  config: PlaygroundConfig;
  instructions: string | null;
  audience: number;
  audienceLabel: (code: number) => string;
  onAudience: (code: number) => void;
  canEdit: boolean;
}

/** Left aside: model/answer settings (saved to the agent) + the simulated audience (per request). */
export function ConfigPanel({ config, instructions, audience, audienceLabel, onAudience, canEdit }: ConfigPanelProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const models = useModels(canEdit);
  const { update } = useSettingsMutations(config.id);
  const [base, setBase] = useState(() => draftOf(config, instructions));
  const [draft, setDraft] = useState(base);
  const dirty = Object.keys(patchOf(draft, base)).length > 0;
  // What the simulated visitor really reads: their audiences capped by the agent's "Who can chat".
  const reads = effectiveAudiences(viewerAudiences(audience), config.audiences);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => { setDraft((d) => ({ ...d, [key]: value })); };
  const selected = models.data?.find((m) => draft.model_id !== "" && m.id === draft.model_id) ?? models.data?.find((m) => m.is_default);
  const readOnlyModel = { id: draft.model_id, display_name: config.model_display_name ?? t("build.playground.chat.defaultModel") };
  const save = () => {
    update.mutate(patchOf(draft, base), {
      onSuccess: (next) => { const fresh = draftOf(next, next.instructions); setBase(fresh); setDraft(fresh); toast.success(t("build.playground.config.saved", { name: next.name })); },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  const saveButton = (
    <Button size="sm" className="flex-1" onClick={save} disabled={!canEdit || !dirty} loading={update.isPending}>
      <Icon name="check" />
      {t("build.playground.config.save")}
    </Button>
  );
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="playground-config">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-bold"><Icon name="sliders-horizontal" className="size-4 text-fg-subtle" />{t("build.playground.config.title")}</h2>
        {dirty ? <Badge tone="warning">{t("build.playground.config.unsaved")}</Badge> : null}
      </div>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
        {dirty ? <p className="rounded-md bg-accent-soft px-3 py-2 text-[11px] text-accent-ink" role="status">{t("build.playground.config.unsavedHint")}</p> : null}
        <Field label={t("build.playground.config.model")} hint={selected?.credits_per_message === null || !selected ? undefined : t("build.playground.config.creditsHint", { count: selected.credits_per_message })}>
          <Select value={draft.model_id !== "" ? draft.model_id : (selected?.id ?? "")} disabled={!canEdit} onChange={(e) => { set("model_id", e.target.value); }}>
            {models.data ? models.data.map((m) => (
              <option key={m.id} value={m.id} disabled={!m.allowed}>{m.descriptor ? t("build.playground.config.modelOption", { name: m.display_name, descriptor: m.descriptor }) : m.display_name}</option>
            )) : <option value={readOnlyModel.id}>{readOnlyModel.display_name}</option>}
          </Select>
        </Field>
        <Slider label={t("build.playground.config.temperature")} value={draft.temperature} onValueChange={(v) => { set("temperature", v); }} min={0} max={1} step={TEMPERATURE_STEP} formatValue={(v) => v.toFixed(1)} startLabel={t("build.playground.config.precise")} endLabel={t("build.playground.config.creative")} disabled={!canEdit} />
        <div className="flex items-start justify-between gap-3">
          <div>
            <label htmlFor="pg-strict" className="text-xs font-semibold">{t("build.playground.config.strict")}</label>
            <p id="pg-strict-hint" className="hint mt-0.5">{t("build.playground.config.strictHint")}</p>
          </div>
          <Switch id="pg-strict" aria-describedby="pg-strict-hint" className="mt-0.5" checked={draft.strict} onCheckedChange={(v) => { set("strict", v); }} disabled={!canEdit} />
        </div>
        <div>
          <span className="label">{t("build.playground.config.citation")}</span>
          <Segmented aria-label={t("build.playground.config.citation")} className="w-full" value={draft.citation_style} onValueChange={(v) => { if (canEdit) set("citation_style", v); }} options={CITATIONS.map((c) => ({ value: c, label: t(`build.playground.config.citations.${c}`) }))} />
        </div>
        <Field label={t("build.playground.config.audience")} hint={t("build.playground.config.audienceHint")}>
          <Select value={audience} onChange={(e) => { onAudience(Number(e.target.value)); }}>
            {AUDIENCES.map((code) => <option key={code} value={code}>{audienceLabel(code)}</option>)}
          </Select>
        </Field>
        <p className="-mt-3 text-[11px] text-fg-muted" role="status" data-testid="playground-effective">
          {reads.length > 0 ? t("build.playground.config.audienceEffective", { labels: reads.map(audienceLabel).join(", ") }) : t("build.playground.config.audienceClosed")}
        </p>
        <Field label={t("build.playground.config.language")}>
          <Select value={draft.reply_in_user_language ? "match" : "english"} disabled={!canEdit} onChange={(e) => { set("reply_in_user_language", e.target.value === "match"); }}>
            <option value="match">{t("build.playground.config.languages.match")}</option>
            <option value="english">{t("build.playground.config.languages.english")}</option>
          </Select>
        </Field>
        {draft.instructions === null ? null : <div>
          <div className="flex items-center justify-between">
            <label htmlFor="pg-instructions" className="label mb-0">{t("build.playground.config.instructions")}</label>
            <span className="text-[11px] tabular-nums text-fg-subtle">{t("build.playground.config.tokens", { count: estimateTokens(draft.instructions) })}</span>
          </div>
          <Textarea id="pg-instructions" rows={11} className="mt-1.5 font-mono text-[11px]" value={draft.instructions} maxLength={8000} disabled={!canEdit} onChange={(e) => { set("instructions", e.target.value); }} />
        </div>}
      </div>
      <div className="flex items-center gap-2 border-t border-line px-4 py-3">
        <Button variant="ghost" size="sm" disabled={!dirty} onClick={() => { setDraft(base); toast.info(t("build.playground.config.resetDone")); }}>
          <Icon name="rotate-ccw" />
          {t("build.playground.config.reset")}
        </Button>
        {canEdit ? saveButton : <Tooltip content={t("build.noPermission")}><span className="flex flex-1">{saveButton}</span></Tooltip>}
      </div>
    </div>
  );
}
